/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import debounce from 'lodash.debounce';
import isNull from 'lodash.isnull';
import dynamic from 'next/dynamic';
import {AlertNotification} from '@ion/core/src/types/entities';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {AssetLayer} from '@ion/features/src/maps/layers/AssetLayer';
import {BasemapLayer} from '@ion/features/src/maps/layers/BasemapLayer';
import {MapToolPositions, MapTools} from '@ion/features/src/maps/types/types';
import {useAuthenticatedUser} from '../admin/users/useAuthenticatedUser';
import {
  NotificationState, setMapBasemap,
  setMapExtent,
  setMapInitialized,
  setMapZoom
} from './state';

;

// Since we're using Next.js, we wrap the SimpleMap import dynamically and set
// ssr to false, as @esri/react-arcgis contains references to the window global
const DynamicSimpleMap = dynamic(() => import('@ion/features/src/maps/SimpleMap'),
  {ssr: false}
);

export interface AlertsNotificationsMapViewProps {
  notification: AlertNotification,
  setIsMapInitialized?: Function;
}

/**
 * The AlertsNotificationsMapView provides the map view for Notifications Page notification view, rendering a SimpleMap
 * instance and configuring the props for the instance based on the notifications reducers
 * state.
 */
export const AlertsNotificationsMapView = ({notification, setIsMapInitialized}: AlertsNotificationsMapViewProps) => {
  const dispatch        = useDispatch();
  const initialized     = useSelector(({notifications: {map}}: NotificationState) => map.initialized);
  const basemap         = useSelector(({notifications: {map}}: NotificationState) => map.basemap);
  const extent          = useSelector(({notifications: {map}}: NotificationState) => map.extent);
  const center          = useSelector(({notifications: {map}}: NotificationState) => map.center);
  const zoom            = useSelector(({notifications: {map}}: NotificationState) => map.zoom);
  const {systemOfUnits} = useAuthenticatedUser();

  const setMapReady = useCallback(function() {
    dispatch(setMapInitialized());
  }, []);

  // invoked when the base map type changes
  const setBasemap = useCallback(function(basemap) {
    dispatch(setMapBasemap(basemap));
  }, []);

  // invoked when the user changes the map extent
  const setExtent = useCallback(debounce(function(val) {
    dispatch(setMapExtent(val));
  }, 250), []);

  // invoked when the user changes the map zoom
  const setZoom = useCallback(debounce(function(val) {
    dispatch(setMapZoom(val));
  }, 250), []);

  useEffect(() => {
    if (isNull(notification?.latitude) || isNull(notification?.longitude)) {
      dispatch(setMapInitialized());
    }
  }, [notification]);

  useEffect(() => {
    initialized && setIsMapInitialized(initialized);
  }, [initialized]);

  const layers = useMemo(() => ([
    {
      id        : 'ctcadmin-map-layer-basemap',
      component : BasemapLayer,
      props     : {
        tools : [{
          id       : MapTools.basemapGallery,
          position : MapToolPositions.topRight,
          props    : {
            onBasemapChanged : setBasemap
          }
        }, {
          id       : MapTools.zoomInOut,
          position : MapToolPositions.bottomRight
        }, {
          id       : MapTools.scaleBar,
          position : MapToolPositions.bottomRight,
          props    : {
            systemOfUnits
          }
        }],
        basemap
      }
    },
    {
      id        : 'ctcadmin-map-layer-asset',
      component : AssetLayer,
      props     : {
        assets        : [notification],
        selectedAsset : notification
      }
    }
  ]), [
    setBasemap,
    AssetLayer,
    notification,
    basemap
  ]);

  const layerCommonProps = null;

  return (
    <>
      {!initialized && <ContentLoader />}
      <DynamicSimpleMap id='dashboard-map'
        initialized={initialized}
        basemap={basemap}
        extent={extent}
        center={center}
        zoom={zoom}
        layers={layers}
        layerCommonProps={layerCommonProps}
        onMapReady={setMapReady}
        onBasemapChanged={setBasemap}
        onExtentChanged={setExtent}
        onPopup={true}
        onZoomChanged={setZoom} />
    </>
  );
};

export default AlertsNotificationsMapView;
